import { groupBy } from 'lodash';
import { CampaignChannels, Send } from 'domains/campaigns/types';
import {
    BooleanOperator,
    EngagementAPIOperator,
    EngagementName,
    EngagementUIOperator,
    EventSelector,
    InclusionAPIOperator,
    InclusionUIOperator,
    isDateRangeOperator,
    isRelativeDateRangeType,
    RecordTypeFilter,
    Segment,
    NestedSegmentDefinitionInput,
    SegmentDemographic,
    SegmentDemographicInput,
    SegmentEngagement,
    SegmentEvent,
    SegmentEventInput,
    SegmentFilter,
    SegmentFilterCategorySpecificField,
    SegmentFilterDemographicFields,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
    SegmentFilterState,
    SegmentFilterValue,
    ChannelSegmentFilter,
    isChannelSegmentFilterArray,
} from 'domains/segments/types';
import { FilterCategory, URLPaths } from 'models/enums';
import { isRootFolder } from 'utils/folders';

export const engagementUiToApiOperator: Record<EngagementUIOperator, EngagementAPIOperator> = {
    [EngagementUIOperator.DID_CLICK]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.DID_DELIVER]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.DID_OPEN]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.DID_RECEIVE]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.DID_REPLY]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.DID_NOT_CLICK]: EngagementAPIOperator.DOES_NOT_CONTAIN,
    [EngagementUIOperator.DID_NOT_DELIVER]: EngagementAPIOperator.DOES_NOT_CONTAIN,
    [EngagementUIOperator.DID_NOT_OPEN]: EngagementAPIOperator.DOES_NOT_CONTAIN,
    [EngagementUIOperator.DID_NOT_RECEIVE]: EngagementAPIOperator.DOES_NOT_CONTAIN,
    [EngagementUIOperator.DID_NOT_REPLY]: EngagementAPIOperator.DOES_NOT_CONTAIN,
    [EngagementUIOperator.CALL_ANSWERED]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.CALL_NOT_ANSWERED]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.VOICEMAIL]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.CALL_FAILED]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.CALL_TRANSFERRED]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.SENT]: EngagementAPIOperator.CONTAINS,
    [EngagementUIOperator.NOT_SENT]: EngagementAPIOperator.DOES_NOT_CONTAIN,
};

export const engagementApiToUiOperator: Record<EngagementName, Record<EngagementAPIOperator, EngagementUIOperator>> = {
    [EngagementName.CLICKED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_CLICK,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_CLICK,
    },
    [EngagementName.DELIVERED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_DELIVER,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_DELIVER,
    },
    [EngagementName.OPENED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_OPEN,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_OPEN,
    },
    [EngagementName.RECEIVED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_RECEIVE,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_RECEIVE,
    },
    [EngagementName.REPLIED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_REPLY,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_REPLY,
    },
    [EngagementName.CALL_ANSWERED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_ANSWERED,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: null,
    },
    [EngagementName.NOT_PICKED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_NOT_ANSWERED,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: null,
    },
    [EngagementName.VOICEMAIL_SENT_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.VOICEMAIL,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: null,
    },
    [EngagementName.CALL_FAILED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_FAILED,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: null,
    },
    [EngagementName.CALL_TRANSFERED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_TRANSFERRED,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: null,
    },
    [EngagementName.SENT_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.SENT,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.NOT_SENT,
    },
};

export const eventApiToUiOperator: Record<string, EventSelector> = {
    exists: EventSelector.INCLUDES,
    'not exists': EventSelector.DOES_NOT_INCLUDE,
};

export const isRelativeDateRangeToday = (value: SegmentFilterValue) =>
    isRelativeDateRangeType(value) && value.duration.value === 0 && value.offset.value === 0;

export const isRelativeDateRangeNext = (value: SegmentFilterValue) =>
    isRelativeDateRangeType(value) &&
    !isRelativeDateRangeToday(value) &&
    value.duration.value !== null &&
    value.duration.value >= 0 &&
    value.offset.value >= 0;

export const isRelativeDateRangeLast = (value: SegmentFilterValue) =>
    isRelativeDateRangeType(value) &&
    !isRelativeDateRangeToday(value) &&
    value.duration.value !== null &&
    value.duration.value >= 0 &&
    value.duration.value + value.offset.value === 0;

export const hasInvalidRelativeDates = (segment: Segment) => {
    if (!segment || !segment.segmentDefinition) {
        return false;
    }

    return !!segment.segmentDefinition.demographic?.find(
        ({ operator, value }) =>
            isDateRangeOperator(operator) &&
            isRelativeDateRangeType(value) &&
            !isRelativeDateRangeNext(value) &&
            !isRelativeDateRangeLast(value) &&
            !isRelativeDateRangeToday(value)
    );
};

export const getDemographicFilters = ({
    hasAllContacts,
    recordTypes,
    segmentRecordTypes,
}: {
    hasAllContacts: boolean;
    recordTypes: RecordTypeFilter['recordType'][];
    segmentRecordTypes: RecordTypeFilter[];
}) => {
    const getFilters = (recordTypeFilters: RecordTypeFilter[]) =>
        recordTypeFilters.flatMap((recordTypeElement) => recordTypeElement.filters);

    const getRecordTypeFiltersBySelectedRecordTypes = (recordTypeFilters: RecordTypeFilter[]) =>
        recordTypeFilters.filter((segmentRecordType) => recordTypes.includes(segmentRecordType.recordType));

    const filters =
        (hasAllContacts
            ? getFilters(segmentRecordTypes)
            : getFilters(getRecordTypeFiltersBySelectedRecordTypes(segmentRecordTypes))) ?? [];

    const uniqueFiltersByName = filters.reduce<Record<string, SegmentFilter>>((acc, filter) => {
        acc[filter.name] = filter;
        return acc;
    }, {});

    return Object.values(uniqueFiltersByName);
};

export const getInclusionOperatorFromAPIToUI = (operator: SegmentDemographic['operator']) => {
    if (operator === InclusionAPIOperator.IN) return InclusionUIOperator.IS_IN;
    if (operator === InclusionAPIOperator.NOT_IN) return InclusionUIOperator.IS_NOT_IN;
    return operator;
};

export const getInclusionOperatorFromUIToAPI = (
    operator: SegmentFilterState<SegmentFilterDemographicFields>['operator']
) => {
    if (operator === InclusionUIOperator.IS_IN) return InclusionAPIOperator.IN;
    if (operator === InclusionUIOperator.IS_NOT_IN) return InclusionAPIOperator.NOT_IN;
    return operator;
};

export const getSegmentDefinition = ({
    filters,
    hasAllContacts,
    matchType,
    recordTypes,
}: {
    filters: SegmentFilterState<SegmentFilterCategorySpecificField>[];
    hasAllContacts: boolean;
    matchType: 'AND' | 'OR';
    recordTypes: RecordTypeFilter['recordType'][];
}): NestedSegmentDefinitionInput => {
    const categoryMap = {
        [FilterCategory.CONTACT_ACTIVITY]: 'engagement',
        [FilterCategory.CONTACT_DETAILS]: 'demographic',
        [FilterCategory.CONTACT_EVENTS]: 'contactEvent',
    };

    const { contactEvent = [], demographic = [], engagement = [] } = groupBy(
        filters,
        (filter) => categoryMap[filter.category] || ''
    );

    return {
        matchType,
        ...(!hasAllContacts && { contactRecordType: recordTypes }),
        contactEvent: (contactEvent as SegmentFilterState<SegmentFilterEventFields>[]).map(convertFilterToEvent),
        demographic: (demographic as SegmentFilterState<SegmentFilterDemographicFields>[]).map(
            convertFilterToDemographic
        ),
        engagement: (engagement as SegmentFilterState<SegmentFilterEngagementFields>[]).map(convertFilterToEngagement),
    };
};

export const convertFilterToDemographic = (
    filter: SegmentFilterState<SegmentFilterDemographicFields>
): SegmentDemographicInput => ({
    includeNullValues: filter.includeNullValues,
    name: filter.name,
    operator: getInclusionOperatorFromUIToAPI(filter.operator) as SegmentDemographic['operator'],
    value: filter.categorySpecificFields.value,
});

export const convertFilterToEngagement = (
    filter: SegmentFilterState<SegmentFilterEngagementFields>
): SegmentEngagement => {
    const getAdditionalValues = () => {
        const autocompleteValue = filter.categorySpecificFields?.sendInteractionField?.autocompleteValue;
        if (Array.isArray(autocompleteValue)) return autocompleteValue.map((value) => value.value);
        return undefined;
    };
    const additionalValues = getAdditionalValues();

    return {
        ...(additionalValues && { additionalValues: additionalValues as string[] }),
        channel: filter.categorySpecificFields?.channel,
        name: filter.name as EngagementName,
        operator: engagementUiToApiOperator[filter.operator as EngagementUIOperator] as EngagementAPIOperator,
        value: filter.categorySpecificFields.value as number,
        segmentLinkValue: filter.categorySpecificFields.sendInteractionField?.value as string,
    };
};

export const convertFilterToEvent = (filter: SegmentFilterState<SegmentFilterEventFields>): SegmentEventInput => {
    const getConditionsWithoutAutocomplete = (conditions: SegmentFilterEventFields['conditions']) =>
        conditions?.map(({ autocompleteValue, autocompleteInputValue, ...condition }) => condition) ?? [];

    const getConditionsWithOperatorsFromUIToAPI = (conditions: any): SegmentDemographicInput[] =>
        conditions?.map((condition: any) => ({
            ...condition,
            operator: getInclusionOperatorFromUIToAPI(condition.operator) as SegmentDemographic['operator'],
        })) ?? [];

    const getValue = (value: SegmentFilterEventFields['value']): SegmentDemographicInput[] =>
        value
            ? [
                  {
                      name: 'start',
                      operator: filter.categorySpecificFields?.dateRangeDateTimeOperator,
                      value,
                  },
              ]
            : [];

    const getSelectorFromUIToAPI = (operator: SegmentFilterState<SegmentFilterEventFields>['operator']) => {
        if (operator === EventSelector.INCLUDES || operator === EventSelector.DOES_NOT_INCLUDE) return 'any';
    };

    const getOperatorFromUIToAPI = (operator: SegmentFilterState<SegmentFilterEventFields>['operator']) => {
        if (operator === EventSelector.INCLUDES) return 'exists';
        if (operator === EventSelector.DOES_NOT_INCLUDE) return 'not exists';
    };

    const otherConditions =
        getConditionsWithOperatorsFromUIToAPI(
            getConditionsWithoutAutocomplete(filter.categorySpecificFields?.conditions)
        ) ?? [];

    return {
        conditions: [
            {
                name: 'record_type',
                operator: BooleanOperator.IS_EQUAL_TO,
                value: filter.name,
            },
            ...otherConditions,
            ...getValue(filter.categorySpecificFields?.value),
        ],
        selector: getSelectorFromUIToAPI(filter.operator),
        operator: getOperatorFromUIToAPI(filter.operator),
    };
};

export const getEventFilter = (event: SegmentEvent, id: number): SegmentFilterState<SegmentFilterEventFields> => {
    // Map selector + operator to UI operator (matching EventDetails.tsx logic)
    // For 'any' selector: exists -> INCLUDES, not exists -> DOES_NOT_INCLUDE
    // For other selectors (first, most recent, next, last), default to INCLUDES for now
    // TODO: handle upcoming possible selectors (most recent, next, last, etc) with switch/case
    const getOperatorFromSelectorAndOperator = (selector: string, operator?: string): EventSelector => {
        const isAnySelector = selector === 'any';
        if (isAnySelector && (!operator || operator === 'exists')) return EventSelector.INCLUDES;
        if (isAnySelector && operator === 'not exists') return EventSelector.DOES_NOT_INCLUDE;
        // For other selectors, use operator mapping if available, otherwise default to INCLUDES
        return operator ? eventApiToUiOperator[operator] || EventSelector.INCLUDES : EventSelector.INCLUDES;
    };

    const operator = getOperatorFromSelectorAndOperator(event.selector, event.operator);
    const recordType = event.conditions.find((condition) => condition.name === 'record_type');

    const conditionsFiltered = event.conditions.filter((condition) => condition.name !== 'record_type');

    const conditions = conditionsFiltered.map((condition) => ({
        ...condition,
        operator: getInclusionOperatorFromAPIToUI(condition.operator),
        autocompleteValue: Array.isArray(condition.value)
            ? condition.value.map((value) => ({
                  label: `${value}`,
                  value,
              }))
            : { label: `${condition.value}`, value: `${condition.value}` },
        ...(!isDateRangeOperator(operator) &&
            !Array.isArray(condition.value) && {
                autocompleteInputValue: `${condition.value}`,
            }),
    }));

    return {
        category: FilterCategory.CONTACT_EVENTS,
        categorySpecificFields: {
            conditions,
            value: '',
        },
        id,
        name: `${recordType?.value}`,
        operator,
    };
};

export const getDemographicFilter = (
    demographic: SegmentDemographic,
    id: number
): SegmentFilterState<SegmentFilterDemographicFields> => ({
    category: FilterCategory.CONTACT_DETAILS,
    categorySpecificFields: {
        value: demographic.value,
    },
    id,
    includeNullValues: demographic.includeNullValues,
    name: demographic.name,
    operator: getInclusionOperatorFromAPIToUI(demographic.operator),
    autocompleteValue: Array.isArray(demographic.value)
        ? demographic.value.map((value) => ({
              label: `${value}`,
              value,
          }))
        : { label: `${demographic.value}`, value: `${demographic.value}` },
    ...(!isDateRangeOperator(demographic.operator) &&
        !Array.isArray(demographic.value) && {
            autocompleteInputValue: `${demographic.value}`,
        }),
});

export const getEngagementFilter = (
    engagement: SegmentEngagement,
    id: number,
    sends: Send[]
): SegmentFilterState<SegmentFilterEngagementFields> => {
    const selectedSend: Send | undefined = sends?.find((send) => send.id === engagement.value);

    const hasSegmentLinkValue = engagement.segmentLinkValue !== undefined && engagement.segmentLinkValue !== null;
    const hasAdditionalValues = engagement.additionalValues && engagement.additionalValues.length > 0;

    return {
        ...engagement,
        category: FilterCategory.CONTACT_ACTIVITY,
        categorySpecificFields: {
            value: engagement.value,
            sendInteractionField:
                hasSegmentLinkValue || hasAdditionalValues
                    ? {
                          value: hasSegmentLinkValue ? engagement.segmentLinkValue : undefined,
                          autocompleteInputValue: hasSegmentLinkValue ? engagement.segmentLinkValue : '',
                          autocompleteValue: hasSegmentLinkValue
                              ? { label: engagement.segmentLinkValue, value: engagement.segmentLinkValue }
                              : engagement?.additionalValues?.map((value) => ({
                                    label: value,
                                    value,
                                })),
                      }
                    : undefined,
            channel: engagement.channel ?? CampaignChannels.EMAIL,
        },
        id,
        name: engagement.name,
        operator: engagementApiToUiOperator[engagement.name][engagement.operator],
        // Only set autocompleteValue if we found a valid send with a name and id
        // If value is null or send is not found, set to null to avoid showing "[object object]"
        autocompleteInputValue: selectedSend?.name ?? '',
        autocompleteValue:
            selectedSend?.name && selectedSend?.id
                ? {
                      label: selectedSend.name,
                      value: selectedSend.id,
                  }
                : null,
    };
};

export const getSegmentCreatePath = (parentFolderId?: number) => {
    const queryParams = parentFolderId && !isRootFolder(parentFolderId) ? `?parentFolderId=${parentFolderId}` : '';
    return `${URLPaths.SEGMENT_CREATE}${queryParams}`;
};

/**
 * Helper function to generate engagementNameToValuesRef mapping from engagement filters.
 * Handles both SegmentFilter[] and ChannelSegmentFilter[] types.
 * For ChannelSegmentFilter[], uses sendsRef from SegmentFilterV2.
 * For SegmentFilter[], uses valuesRef directly.
 */
export const getEngagementNameToValuesRef = (
    engagementFilters: SegmentFilter[] | ChannelSegmentFilter[] | undefined
): Record<string, string> => {
    if (!engagementFilters || engagementFilters.length === 0) {
        return {};
    }

    if (isChannelSegmentFilterArray(engagementFilters)) {
        // Handle ChannelSegmentFilter[]
        return engagementFilters.reduce((acc: Record<string, string>, channelFilter) => {
            channelFilter.filters.forEach((filter) => {
                // SegmentFilterV2 uses sendsRef instead of valuesRef
                acc[filter.name] = filter.sendsRef;
            });
            return acc;
        }, {});
    } else {
        // Handle SegmentFilter[]
        return engagementFilters.reduce((acc: Record<string, string>, filter) => {
            acc[filter.name] = filter.valuesRef;
            return acc;
        }, {});
    }
};
